export interface MediaMTXCameraOptions {
  streamPath: string;
  videoDeviceId?: string;
  token: string;
}

export interface MediaMTXCameraSession {
  stream: MediaStream;
  stop: () => void;
}

const CAMERA_SERVER = 'https://camera.orrynox.de';

export type NoxburyBrowser =
  | 'chrome'
  | 'edge'
  | 'firefox'
  | 'safari'
  | 'other';

export interface CameraDevice {
  deviceId: string;
  label: string;
}

export interface CameraBrowserHelp {
  browser: NoxburyBrowser;
  browserName: string;
  allowText: string;
  rejectedText: string;
}

export function detectBrowser(): NoxburyBrowser {
  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes('edg/')) {
    return 'edge';
  }

  if (ua.includes('firefox/')) {
    return 'firefox';
  }

  if (
    ua.includes('safari/') &&
    !ua.includes('chrome/') &&
    !ua.includes('chromium/')
  ) {
    return 'safari';
  }

  if (
    ua.includes('chrome/') ||
    ua.includes('chromium/')
  ) {
    return 'chrome';
  }

  return 'other';
}

export function getCameraBrowserHelp(): CameraBrowserHelp {
  const browser = detectBrowser();

  switch (browser) {
    case 'chrome':
      return {
        browser,
        browserName: 'Google Chrome',
        allowText:
          'Wenn Chrome nach dem Kamerazugriff fragt, wähle „Zulassen“.',
        rejectedText:
          'Falls du die Kamera abgelehnt hast: Klicke links neben der Webadresse auf das Symbol für Website-Einstellungen, öffne die Website-Einstellungen und stelle „Kamera“ auf „Zulassen“. Lade Noxbury danach neu.',
      };

    case 'edge':
      return {
        browser,
        browserName: 'Microsoft Edge',
        allowText:
          'Wenn Edge nach dem Kamerazugriff fragt, wähle „Zulassen“.',
        rejectedText:
          'Falls du die Kamera abgelehnt hast: Klicke links neben der Webadresse auf das Symbol für Websiteinformationen bzw. Berechtigungen und stelle „Kamera“ auf „Zulassen“. Lade Noxbury danach neu.',
      };

    case 'firefox':
      return {
        browser,
        browserName: 'Mozilla Firefox',
        allowText:
          'Wenn Firefox nach dem Kamerazugriff fragt, wähle die gewünschte Kamera und klicke auf „Erlauben“.',
        rejectedText:
          'Falls du die Kamera blockiert hast: Klicke links neben der Webadresse auf das Berechtigungs-Symbol und entferne die blockierte Kamera-Berechtigung. Starte die Kamera danach erneut und erlaube den Zugriff.',
      };

    case 'safari':
      return {
        browser,
        browserName: 'Safari',
        allowText:
          'Wenn Safari nach dem Kamerazugriff fragt, erlaube Noxbury den Zugriff auf die Kamera.',
        rejectedText:
          'Falls du die Kamera abgelehnt hast: Öffne Safari → Einstellungen → Websites → Kamera und erlaube den Zugriff für Noxbury. Lade die Seite danach neu.',
      };

    default:
      return {
        browser,
        browserName: 'deinem Browser',
        allowText:
          'Erlaube deinem Browser den Zugriff auf die Kamera, wenn du danach gefragt wirst.',
        rejectedText:
          'Falls du den Zugriff abgelehnt hast, öffne die Website- bzw. Berechtigungseinstellungen deines Browsers und erlaube dort den Kamerazugriff für Noxbury. Lade die Seite danach neu.',
      };
  }
}

export async function getVideoDevices(): Promise<CameraDevice[]> {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return [];
  }

  const devices =
    await navigator.mediaDevices.enumerateDevices();

  let cameraNumber = 0;

  return devices
    .filter((device) => device.kind === 'videoinput')
    .map((device) => {
      cameraNumber += 1;

      return {
        deviceId: device.deviceId,
        label:
          device.label ||
          `Kamera ${cameraNumber}`,
      };
    });
}

function waitForIceGatheringComplete(
  peerConnection: RTCPeerConnection,
): Promise<void> {
  if (peerConnection.iceGatheringState === 'complete') {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const checkState = () => {
      if (peerConnection.iceGatheringState === 'complete') {
        peerConnection.removeEventListener(
          'icegatheringstatechange',
          checkState,
        );

        resolve();
      }
    };

    peerConnection.addEventListener(
      'icegatheringstatechange',
      checkState,
    );
  });
}

export async function openMediaMTXCamera(
  options: MediaMTXCameraOptions,
): Promise<MediaMTXCameraSession> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error(
      'Dieser Browser unterstützt keinen Kamera-Zugriff.',
    );
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    video: {
      deviceId: options.videoDeviceId
        ? { exact: options.videoDeviceId }
        : undefined,
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: {
        ideal: 30,
        max: 30,
      },
    },

    // Noxbury überträgt ausschließlich das Kamerabild.
    // Audio läuft weiterhin über Discord/den normalen Stream-Weg.
    audio: false,
  });

  const peerConnection = new RTCPeerConnection();

  try {
    for (const track of stream.getTracks()) {
      peerConnection.addTrack(track, stream);
    }

    const offer =
      await peerConnection.createOffer();

    await peerConnection.setLocalDescription(
      offer,
    );

    await waitForIceGatheringComplete(
      peerConnection,
    );

    const localDescription =
      peerConnection.localDescription;

    if (
      !localDescription ||
      !localDescription.sdp
    ) {
      throw new Error(
        'WebRTC-Angebot konnte nicht erstellt werden.',
      );
    }

    const whipUrl =
      `${CAMERA_SERVER}/${encodeURIComponent(
        options.streamPath,
      )}/whip`;

    const response = await fetch(
      whipUrl,
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/sdp',
          Authorization:
            `Bearer ${options.token}`,
        },

        body: localDescription.sdp,
      },
    );

    if (!response.ok) {
      const responseText =
        await response.text();

      throw new Error(
        `MediaMTX hat die Kamera abgelehnt (${response.status})${
          responseText
            ? `: ${responseText}`
            : ''
        }`,
      );
    }

    const answerSdp =
      await response.text();

    await peerConnection.setRemoteDescription({
      type: 'answer',
      sdp: answerSdp,
    });

    let stopped = false;

    const stop = () => {
      if (stopped) {
        return;
      }

      stopped = true;

      for (const track of stream.getTracks()) {
        track.stop();
      }

      peerConnection.close();
    };

    return {
      stream,
      stop,
    };
  } catch (error) {
    for (const track of stream.getTracks()) {
      track.stop();
    }

    peerConnection.close();

    throw error;
  }
}

export function playerCameraPath(
  lobbyCode: string,
  playerId: string,
): string {
  const safeLobby =
    lobbyCode.replace(
      /[^a-zA-Z0-9_-]/g,
      '',
    );

  const safePlayer =
    playerId.replace(
      /[^a-zA-Z0-9_-]/g,
      '',
    );

  return `noxbury-${safeLobby}-${safePlayer}`;
}

export function playerCameraUrl(
  lobbyCode: string,
  playerId: string,
): string {
  return `${CAMERA_SERVER}/${playerCameraPath(
    lobbyCode,
    playerId,
  )}`;
}

/**
 * Eindeutiger MediaMTX-Pfad für die Kamera des Spielleiters.
 */
export function hostCameraPath(
  lobbyCode: string,
): string {
  const safeLobby =
    lobbyCode.replace(
      /[^a-zA-Z0-9_-]/g,
      '',
    );

  return `noxbury-${safeLobby}-host`;
}

/**
 * URL, unter der MediaMTX den Host-Stream bereitstellt.
 */
export function hostCameraUrl(
  lobbyCode: string,
): string {
  return `${CAMERA_SERVER}/${hostCameraPath(
    lobbyCode,
  )}`;
}
